// Admin → Import, Step 3 and the run: the decisions the plan needs (each with a default, so Next
// works without reading), each row's fields once they are applied, and the run plan — the records a
// run writes, in order: list values, lines, pickups, shipping rows. Pure: the page writes the plan
// (`import-run.ts`), one record per call through `logged-write.ts`.

import type { ListName } from "../entities";
import { ROUTING_LINES } from "../entities";
import type { ApprovalOverrides } from "./approve";
import type { CarrierOption } from "./carriers";
import type { LineSource } from "./deposco";
import { FIELD_LIST, cleanListValue, joinValues, sameListValue } from "./lists";
import { PE_ASAP } from "./pe-order";
import { routingStatusValues } from "./routing-status";
import type { ListValueRecord, WarehouseField } from "./types";
import type { ImportConflict, ImportLine, ImportPlan, LineGroup, RowPlan } from "./workbook-import";
import { datesInText, estimateOf, leadDate, lineGroups, listParts, placeOf, rowId } from "./workbook-import";

/** `groupedBy` and `reviewedBy` of a line the import made: what the Worklist's chip and hover read. */
export const IMPORT_BY = "Workbook import";
export const isImportedLine = (l: { stored: { groupedBy?: string | null } | null }) =>
  l.stored?.groupedBy === IMPORT_BY;

/** A list value the app does not know: one of the list's (the look-alike preselected), added, or text. */
export type ValueChoice = { map: string } | { add: true } | { text: true };

export interface ImportDecisions {
  /** Rows naming the same pick tickets / SOs, landing on different lines (`conflictId`): the row that wins, or "skip". */
  conflicts: Record<string, string>;
  /** Rows on one line whose entries differ (`LineGroup`, by its id): the row whose fields win, and whether the notes combine. */
  groups: Record<string, { row: string; combine: boolean }>;
  /** A PO on several open Deposco lines, no DC named (by row id): the line, or "skip". */
  ambiguous: Record<string, string>;
  /** Lines that already have a record: keep what the app has, or the file's typed fields over them. */
  exists: "skip" | "overwrite";
  /** Rows whose QTY / FIRST SHIP / CANCEL differ from the source: approved as the app has them, or UPDATED. */
  differences: Record<string, "approve" | "updated">;
  /** A date column holding prose (`<row id>|<field>`): a day it names, the text to Notes, or empty. */
  prose: Record<string, string>;
  /** Each row's routing status and its day, preselected by the rule (`routingGuess`). */
  routing: Record<string, { status: string; on: string }>;
  /** A list value the app does not know (`<list>|<VALUE>`). */
  values: Record<string, ValueChoice>;
  /** Text over a field's limit (`<row id>|<field>`): cut, the rest to Notes; or the text as edited. */
  overLimit: Record<string, { cut: true } | { edit: string }>;
  /** The check-off's headerless column after INV.: appended to Notes, or ignored. */
  columnS: "append" | "ignore";
  /** A schedule carrier text: a carrier of `routing-carriers` (its key), or "none" (the text to the pickup's Notes). */
  carriers: Record<string, string>;
}

export const PROSE_TO_NOTES = "notes";
export const PROSE_EMPTY = "empty";
export const SKIP = "skip";

const DATE_FIELDS: readonly WarehouseField[] = ["readyDate", "schedShip", "peShipDate"];
/** The text fields a row fills, with their limits (`entities.ts`). */
const TEXT_LIMITS: readonly [WarehouseField, number][] = (
  [
    "workNeeded",
    "productionNotes",
    "routingNotes",
    "routingRef",
    "inventoryNotes",
    "lead",
    "timing",
    "notes",
    "pullGroup",
    "locQty",
    "peAssignedTo",
    "peStatus",
    "peComments",
  ] as WarehouseField[]
).map((f) => {
  const def: { name: string; max?: number } | undefined = ROUTING_LINES.fields.find((x) => x.name === f);
  return [f, def?.max ?? 500];
});
const limitOf = (f: WarehouseField) => TEXT_LIMITS.find(([x]) => x === f)?.[1] ?? 500;
/** Where text a field can't take goes: a PE ORDERS row's to PE Comments, any other's to Notes (the "INV." column). */
const notesFieldOf = (kind: RowPlan["kind"]): WarehouseField => (kind === "pe-orders" ? "peComments" : "notes");
/** The fields "combine" joins with " · ": the notes. */
export const NOTES_FIELDS: readonly WarehouseField[] = ["productionNotes", "routingNotes", "notes", "peComments"];
const JOIN = " · ";

const isDayText = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);
const valueKey = (list: ListName, value: string) => `${list}|${value.trim().toUpperCase()}`;

/** A date field's typed value that is no date (prose): its decision's key. */
export const proseKey = (row: RowPlan, field: WarehouseField) => `${rowId(row)}|${field}`;

/** The prose date cells of the rows that land on a line: Step 3's READY DATE / SCHEDULED SHIP DATE panel. */
export function proseCells(rows: readonly RowPlan[]) {
  return rows.flatMap((r) =>
    DATE_FIELDS.flatMap((f) => {
      const v = r.typed[f];
      if (!v || isDayText(v) || (v === PE_ASAP && f !== "schedShip")) return [];
      return [{ row: r, field: f, text: v, dates: datesInText(v, r.year) }];
    }),
  );
}

/** The text cells over their field's limit: Step 3's panel. */
export function overLimitCells(rows: readonly RowPlan[]) {
  return rows.flatMap((r) =>
    TEXT_LIMITS.flatMap(([f, max]) => {
      const v = r.typed[f];
      return v && v.length > max ? [{ row: r, field: f, text: v, max }] : [];
    }),
  );
}

/** The list cells' values the app does not know, by list: Step 3's Work Needed, Lead, Inventory notes, PE ones. */
export function unknownValues(rows: readonly RowPlan[], values: readonly ListValueRecord[]) {
  const out = new Map<string, { list: ListName; value: string; rows: string[] }>();
  for (const r of rows)
    for (const f of LIST_CELLS) {
      const list = FIELD_LIST[f.field];
      if (!list) continue;
      const known = values.filter((v) => v.list === list);
      for (const part of listParts(r.typed[f.field] ?? "", f.field, f.split)) {
        if (sameListValue(part, known)) continue;
        const k = valueKey(list, part);
        const u = out.get(k) ?? { list, value: part, rows: [] };
        u.rows.push(rowId(r));
        out.set(k, u);
      }
    }
  return [...out.values()];
}
const LIST_CELLS: readonly { field: WarehouseField; split: boolean }[] = [
  { field: "workNeeded", split: true },
  { field: "inventoryNotes", split: false },
  { field: "lead", split: false },
  { field: "peStatus", split: false },
  { field: "peAssignedTo", split: true },
];

/**
 * A conflict's decision: one per set of rows naming the same pick tickets / SOs (rows 691 and 1151
 * share 13: one question), by the rows' ids.
 */
export const conflictId = (c: Pick<ImportConflict, "rows">) => c.rows.map(rowId).join(" & ");

/** Step 3's conflicts: each set of rows once, with the pick tickets / SOs and lines it covers. */
export function conflictGroups(plan: Pick<ImportPlan, "conflicts">) {
  const by = new Map<string, { id: string; rows: string[]; refs: string[]; lines: string[] }>();
  for (const c of plan.conflicts) {
    const id = conflictId(c);
    const g = by.get(id) ?? { id, rows: c.rows.map(rowId), refs: [], lines: [] };
    g.refs.push(c.ref);
    if (c.line && !g.lines.includes(c.line)) g.lines.push(c.line);
    by.set(id, g);
  }
  return [...by.values()];
}

/** The rows a run can place on a line: with a line, or candidates (ambiguous). */
export const placeableRows = (plan: ImportPlan) => plan.rows.filter((r) => r.lines.length || r.candidates.length);

/**
 * Every decision at its default (Step 3's first option): a conflict's first row wins; rows on one
 * line that differ — the first row's fields, the notes combined; an ambiguous PO skipped; lines with
 * a record kept as the app has them; differences approved with the app's values; a prose date its
 * first date (none: the text to Notes); the routing status the rule reads; a list value its look-alike
 * (none: text); text over a limit cut, the rest to Notes; column S appended; a carrier the one it maps
 * to (none: its text to the pickup's Notes).
 */
export function defaultDecisions(plan: ImportPlan, ctx: { listValues: readonly ListValueRecord[] }): ImportDecisions {
  const rows = placeableRows(plan);
  const d: ImportDecisions = {
    conflicts: {},
    groups: {},
    ambiguous: {},
    exists: "skip",
    differences: {},
    prose: {},
    routing: {},
    values: {},
    overLimit: {},
    columnS: "append",
    carriers: {},
  };
  for (const g of conflictGroups(plan)) d.conflicts[g.id] = g.rows[0]!;
  for (const r of rows) {
    if (r.outcome === "ambiguous") d.ambiguous[rowId(r)] = SKIP;
    if (r.diffs.length) d.differences[rowId(r)] = "approve";
    if (r.kind !== "pe-orders")
      d.routing[rowId(r)] = { status: r.routing?.status ?? "Not routed", on: r.routing?.on ?? "" };
  }
  for (const g of decisionGroups(plan, d)) d.groups[g.id] = { row: g.rows[0]!, combine: true };
  for (const p of proseCells(rows)) d.prose[proseKey(p.row, p.field)] = p.dates[0] ?? PROSE_TO_NOTES;
  for (const o of overLimitCells(rows)) d.overLimit[`${rowId(o.row)}|${o.field}`] = { cut: true };
  for (const u of unknownValues(rows, ctx.listValues)) {
    const look = plan.unknown.find((x) => x.list === u.list && x.value.toUpperCase() === u.value.toUpperCase());
    d.values[valueKey(u.list, u.value)] = look?.lookAlike ? { map: look.lookAlike } : { text: true };
  }
  for (const e of plan.entries) if (e.carrierText) d.carriers[e.carrierText] = e.carrier?.key ?? "none";
  return d;
}

/** "Accept every look-alike guess": each unknown value that has a look-alike maps to it. */
export function acceptLookAlikes(plan: ImportPlan, d: ImportDecisions, list?: ListName): ImportDecisions {
  const values = { ...d.values };
  for (const u of plan.unknown)
    if (u.lookAlike && (!list || u.list === list)) values[valueKey(u.list, u.value)] = { map: u.lookAlike };
  return { ...d, values };
}

/** The lines a row lands on once its conflicts and its ambiguous PO are decided. */
export function rowLines(plan: ImportPlan, d: Pick<ImportDecisions, "conflicts" | "ambiguous">) {
  // A row that lost a pick ticket or SO loses that one's line; its other lines stay.
  const lost = new Map<string, Set<string>>();
  for (const c of plan.conflicts) {
    const won = d.conflicts[conflictId(c)] ?? SKIP;
    for (const x of c.rows) {
      const id = rowId(x);
      if (id === won) continue;
      lost.set(id, (lost.get(id) ?? new Set<string>()).add(c.line));
    }
  }
  return (r: RowPlan): string[] => {
    if (r.outcome === "ambiguous") {
      const chosen = d.ambiguous[rowId(r)];
      return chosen && chosen !== SKIP && r.candidates.includes(chosen) ? [chosen] : [];
    }
    const gone = lost.get(rowId(r));
    return gone ? r.lines.filter((l) => !gone.has(l)) : r.lines;
  };
}

/** The line groups to decide (rows that differ), with the conflicts and ambiguous POs as decided. */
export function decisionGroups(plan: ImportPlan, d: Pick<ImportDecisions, "conflicts" | "ambiguous">): LineGroup[] {
  return lineGroups(plan.rows, rowLines(plan, d)).filter((g) => !g.equal);
}

/** One row's fields once the decisions apply, the list values it adds, and what the result says of it. */
export interface RowFields {
  fields: Partial<Record<WarehouseField, string>>;
  adds: { list: ListName; value: string }[];
  notes: string[];
}

const capped = (v: string, max: number) => (v.length > max ? v.slice(0, max) : v);
const joined = (...parts: (string | undefined)[]) => parts.filter((p) => p && p.trim()).join(JOIN);

/** The words a row's routing status came from, without the ROUTED / PRE ROUTED part: its Routing Notes. */
export function routingNotesRest(text: string): string {
  return text
    .replace(/\b(?:PRE[\s-]*)?ROUTED\b\s*(?:\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)?/i, " ")
    .replace(/^\d{4}-\d{2}-\d{2}$/, "")
    .replace(/^\d{1,2}\/\d{1,2}(?:\/\d{2,4})?$/, "")
    .replace(/\s+/g, " ")
    .replace(/^[\s,;/·-]+|[\s,;/·-]+$/g, "");
}

/**
 * A row's fields with every decision applied: prose dates, list values (mapped, added, or text into
 * the notes), the routing status, column S, the estimate in brackets of Assigned to, text over a limit.
 */
export function rowFields(row: RowPlan, d: ImportDecisions, values: readonly ListValueRecord[]): RowFields {
  const t = row.typed;
  const id = rowId(row);
  const out: Partial<Record<WarehouseField, string>> = {};
  const adds: RowFields["adds"] = [];
  const notes: string[] = [];
  /** Text a field can't take, into the notes: prose dates, list values kept as text. */
  const extra: string[] = [];
  /** Work Needed's leftovers (a tracking number, a date), into Production Notes. */
  const work: string[] = [];
  const notesField = notesFieldOf(row.kind);

  for (const [f, v] of Object.entries(t) as [WarehouseField, string][]) if (!DATE_FIELDS.includes(f)) out[f] = v;
  // Dates: a day, ASAP where the field takes it, else the prose decision.
  for (const f of DATE_FIELDS) {
    const v = t[f];
    if (!v) continue;
    if (isDayText(v) || (v === PE_ASAP && f !== "schedShip")) {
      out[f] = v;
      continue;
    }
    const choice = d.prose[`${id}|${f}`] ?? PROSE_TO_NOTES;
    if (isDayText(choice)) out[f] = choice;
    else if (choice === PROSE_TO_NOTES) extra.push(v);
  }
  // List values: each part known, mapped, added, or text into the notes.
  const pick = (f: WarehouseField, split: boolean) => {
    const list = FIELD_LIST[f];
    if (!list || !t[f]) return;
    const known = values.filter((v) => v.list === list);
    const kept: string[] = [];
    for (const part of listParts(t[f], f, split)) {
      const same = sameListValue(part, known);
      if (same) {
        kept.push(same.value);
        continue;
      }
      const c = d.values[valueKey(list, part)] ?? { text: true };
      if ("map" in c) kept.push(c.map);
      else if ("add" in c && !part.includes(";")) {
        const value = cleanListValue(part);
        kept.push(value);
        adds.push({ list, value });
      } else if (f === "workNeeded") work.push(part);
      else
        extra.push(
          f === "lead"
            ? `Lead: ${part}`
            : f === "inventoryNotes"
              ? `Inventory: ${part}`
              : f === "peStatus"
                ? `Status: ${part}`
                : `Assigned to: ${part}`,
        );
    }
    out[f] = split ? joinValues(kept) : (kept[0] ?? "");
  };
  pick("workNeeded", true);
  pick("inventoryNotes", false);
  pick("lead", false);
  pick("peStatus", false);
  pick("peAssignedTo", true);
  const since = leadDate(t.lead, row.year);
  if (since) notes.push(`Lead set in the workbook on ${since}`);
  const estimate = estimateOf(t.peAssignedTo);
  if (estimate) out.peEstimate = estimate;
  if (row.kind === "pe-orders") {
    if (work.length) extra.push(...work);
  } else {
    out.productionNotes = joined(t.productionNotes, ...work);
    // The routing status the rule read (or Step 3 chose), and its day; Routing Notes without it.
    const r = d.routing[id] ?? { status: row.routing?.status ?? "Not routed", on: row.routing?.on ?? "" };
    out.routingStatus = r.status;
    out.routedOn = r.status === "Not routed" ? "" : r.on;
    out.routingNotes = routingNotesRest(t.routingNotes ?? "");
    if (d.columnS === "ignore" && row.kind === "production") out.notes = "";
  }
  if (extra.length) out[notesField] = joined(out[notesField], ...extra);
  // Text over a limit: cut, the rest to the notes; or the text as edited.
  for (const [f, max] of TEXT_LIMITS) {
    const v = out[f];
    if (!v || v.length <= max) continue;
    const c = d.overLimit[`${id}|${f}`] ?? { cut: true };
    if ("edit" in c) out[f] = capped(c.edit, max);
    else {
      out[f] = v.slice(0, max);
      if (f !== notesField) out[notesField] = joined(out[notesField], v.slice(max));
    }
  }
  out[notesField] = capped(out[notesField] ?? "", limitOf(notesField));
  if (!out[notesField]) delete out[notesField];
  return { fields: out, adds, notes };
}

/** A line the run writes: create (its first save, Approved by the import), update, or skip. */
export interface RunLine {
  key: string;
  place: string;
  source: LineSource;
  /** Its record, when it has one. */
  id: string | null;
  write: "create" | "update" | "skip";
  fields: Partial<Record<WarehouseField, string>>;
  /** A row decided UPDATED: the file's values in the baseline (`approvalPlan`). New lines only. */
  overrides: ApprovalOverrides | null;
  rows: string[];
  notes: string[];
}

export interface RunPickup {
  /** The entry's tab and Excel row. */
  entry: string;
  day: string;
  input: {
    wh: string;
    pickupDate: string;
    appTime: string;
    carrier: string;
    carrierCode: string;
    carrierSource: LineSource | null;
    door: string;
    loadRef: string;
    notes: string;
    status: "Scheduled";
  };
  rows: {
    line: string;
    ctns: number | null;
    plts: number | null;
    loader: string;
    inTime: string;
    outTime: string;
    trailer: string;
    wmsAsnTrip: string;
    notes: string;
  }[];
}

/** What happens to each row (the result file's Outcome) and to each schedule entry. */
export interface RowResult {
  outcome: string;
  lines: string[];
  note: string;
}

export interface RunPlan {
  values: { list: ListName; value: string }[];
  lines: RunLine[];
  pickups: RunPickup[];
  rows: Record<string, RowResult>;
  entries: Record<string, RowResult>;
  /** The records it writes: list values, lines (and their pick-ticket copies), pickups, shipping rows. */
  total: number;
}

const firstDiff = (rows: readonly RowPlan[], d: ImportDecisions): ApprovalOverrides | null => {
  const r = rows.find((x) => x.diffs.length && d.differences[rowId(x)] === "updated");
  if (!r) return null;
  const o: ApprovalOverrides = {};
  for (const x of r.diffs) {
    if (x.field === "QTY") o.qty = Number(x.file);
    if (x.field === "FIRST SHIP") o.start = x.file;
    if (x.field === "CANCEL") o.cancel = x.file;
  }
  return o;
};

/**
 * The run: every line its rows land on with their fields (rows on one line merged when equal, else
 * as Step 3 decided), the list values added, and the pickups of the schedule with their rows — a
 * line on a pickup takes its date as SCHEDULED SHIP DATE and its PLTS as Pallets, as Shipping does.
 * A line with a record is skipped (or its typed fields written over, `exists`), never its baseline,
 * pick tickets, review or `groupedBy`.
 */
export function runPlan(
  plan: ImportPlan,
  d: ImportDecisions,
  ctx: { lines: readonly ImportLine[]; listValues: readonly ListValueRecord[]; carriers: readonly CarrierOption[] },
): RunPlan {
  const lineOf = new Map(ctx.lines.map((l) => [l.key, l]));
  const linesOfRow = rowLines(plan, d);
  const rows = placeableRows(plan);
  const results: Record<string, RowResult> = {};
  const byLine = new Map<string, { line: RowPlan[]; pe: RowPlan[] }>();
  for (const r of rows) {
    const lines = linesOfRow(r);
    const why =
      r.outcome === "ambiguous" && !lines.length
        ? "Skipped: its PO is on several Deposco lines"
        : !lines.length
          ? "Skipped: lost its pick tickets / SOs to another row"
          : "";
    results[rowId(r)] = { outcome: why ? "Skipped" : "Imported", lines, note: why };
    for (const l of lines) {
      const at = byLine.get(l) ?? { line: [], pe: [] };
      (r.kind === "pe-orders" ? at.pe : at.line).push(r);
      byLine.set(l, at);
    }
  }
  for (const r of plan.rows)
    if (!results[rowId(r)])
      results[rowId(r)] = { outcome: r.outcome === "error" ? "Can't read" : "Dropped", lines: [], note: r.error };

  const adds = new Map<string, { list: ListName; value: string }>();
  const fieldsOfGroup = (key: string, fields: "line" | "pe", rs: readonly RowPlan[]) => {
    const each = rs.map((r) => ({ r, f: rowFields(r, d, ctx.listValues) }));
    for (const x of each) for (const a of x.f.adds) adds.set(`${a.list}|${a.value}`, a);
    if (each.length === 1) return { fields: each[0]!.f.fields, notes: each[0]!.f.notes };
    const choice = d.groups[`${fields}|${key}`];
    const same = new Set(each.map((x) => JSON.stringify(Object.entries(x.f.fields).sort()))).size === 1;
    const winner = (!same && choice && each.find((x) => rowId(x.r) === choice.row)) || each[0]!;
    const out = { ...winner.f.fields };
    if (!same && (choice?.combine ?? true))
      for (const f of NOTES_FIELDS) {
        const all = [...new Set(each.map((x) => x.f.fields[f]).filter((v): v is string => !!v))];
        if (all.length) out[f] = capped(all.join(JOIN), limitOf(f));
      }
    for (const x of each)
      if (x !== winner)
        results[rowId(x.r)] = {
          ...results[rowId(x.r)]!,
          outcome: same ? "Merged" : choice?.combine === false ? "Another row won" : "Combined",
        };
    return { fields: out, notes: each.flatMap((x) => x.f.notes) };
  };

  const runLines = new Map<string, RunLine>();
  for (const [key, g] of byLine) {
    const known = lineOf.get(key);
    const a = g.line.length ? fieldsOfGroup(key, "line", g.line) : { fields: {}, notes: [] };
    const b = g.pe.length ? fieldsOfGroup(key, "pe", g.pe) : { fields: {}, notes: [] };
    const stored = !!known?.stored;
    const all = [...g.line, ...g.pe];
    runLines.set(key, {
      key,
      place: known?.place ?? placeOf(key),
      source: known?.source ?? (key.startsWith("DEP|") ? "Deposco" : "A2000"),
      id: known?.id ?? null,
      write: stored ? (d.exists === "overwrite" ? "update" : "skip") : "create",
      fields: { ...a.fields, ...b.fields },
      overrides: stored ? null : firstDiff(all, d),
      rows: all.map(rowId),
      notes: [
        ...a.notes,
        ...b.notes,
        ...(stored && d.exists === "skip" ? ["Line already saved: kept as the app has it"] : []),
      ],
    });
    if (stored && d.exists === "skip")
      for (const r of all) results[rowId(r)] = { ...results[rowId(r)]!, outcome: "Line already saved: skipped" };
  }

  // The schedule: one pickup per entry that reaches a line, its rows one per line.
  const entries: Record<string, RowResult> = {};
  const pickups: RunPickup[] = [];
  const carrierOf = new Map(ctx.carriers.map((c) => [c.key, c]));
  for (const e of plan.entries) {
    const id = `${e.tab}|${e.n}`;
    if (e.outcome !== "import") {
      entries[id] = {
        outcome: e.outcome === "exists" ? "Pickup already saved: skipped" : "Skipped: no line",
        lines: e.lines,
        note: "",
      };
      continue;
    }
    const carrier = carrierOf.get(d.carriers[e.carrierText] ?? "") ?? null;
    pickups.push({
      entry: id,
      day: e.day,
      input: {
        wh: "PE",
        pickupDate: e.day,
        appTime: e.appTime,
        carrier: carrier?.name ?? "",
        carrierCode: carrier?.code ?? "",
        carrierSource: carrier?.source ?? null,
        door: e.door,
        loadRef: capped(e.loadRef, 100),
        notes: capped(joined(e.notes, carrier || !e.carrierText ? "" : `Carrier: ${e.carrierText}`), 500),
        status: "Scheduled",
      },
      rows: e.shipping.map((s) => ({
        line: s.line,
        ctns: s.ctns,
        plts: s.plts,
        loader: e.loader,
        inTime: e.inTime,
        outTime: e.outTime,
        trailer: e.trailer,
        wmsAsnTrip: e.wmsAsnTrip || (lineOf.get(s.line)?.wms ?? ""),
        notes: "",
      })),
    });
    entries[id] = {
      outcome: "Pickup",
      lines: e.lines,
      note: carrier ? "" : e.carrierText ? "Carrier text in the pickup's Notes" : "",
    };
    // A line on a pickup: SCHEDULED SHIP DATE the pickup's day, Pallets its PLTS — saved with the line,
    // or, on a line the run otherwise skips, only these.
    for (const s of e.shipping) {
      const known = lineOf.get(s.line);
      const line =
        runLines.get(s.line) ??
        ({
          key: s.line,
          place: known?.place ?? placeOf(s.line),
          source: known?.source ?? (s.line.startsWith("DEP|") ? "Deposco" : "A2000"),
          id: known?.id ?? null,
          write: known?.stored ? "skip" : "create",
          fields: {},
          overrides: null,
          rows: [],
          notes: ["On a pickup of the schedule, in no row"],
        } satisfies RunLine);
      const onPickup: Partial<Record<WarehouseField, string>> = {
        schedShip: e.day,
        ...(s.plts !== null ? { pallets: String(s.plts) } : {}),
      };
      if (line.write === "skip") {
        line.write = "update";
        line.fields = onPickup;
      } else line.fields = { ...line.fields, ...onPickup };
      runLines.set(s.line, line);
    }
  }

  const lines = [...runLines.values()];
  const written = lines.filter((l) => l.write !== "skip");
  const copies = written.filter((l) => l.write === "create").reduce((n, l) => n + (lineOf.get(l.key)?.copies ?? 0), 0);
  const values = [...adds.values()];
  return {
    values,
    lines,
    pickups,
    rows: results,
    entries,
    total: values.length + written.length + copies + pickups.length + pickups.reduce((n, p) => n + p.rows.length, 0),
  };
}

/** The Routing status list's values (Step 3's options): the list's records, else the starting seven. */
export const routingStatusOptions = (values: readonly ListValueRecord[]) =>
  routingStatusValues(values)
    .filter((v) => !v.retired)
    .map((v) => v.value);

// ── Runs ─────────────────────────────────────────────────────────────────────────────────────

/** A run's record (`import-run`, 020): counts and summary, never the plan. */
export interface ImportRunRecord {
  id: string;
  fileName: string;
  fileHash: string;
  startedBy: string;
  startedAt: string;
  finishedAt: string | null;
  status: string;
  cutoff: string | null;
  tabs: string;
  counts: string;
  summary: string;
  updated_at?: string;
}

/** What Admin → Import's first screen reads (`GET /api/routing/admin/import/runs`). */
export interface ImportRunsPayload {
  runs: ImportRunRecord[];
  appEnv: string;
  counts: StoreCounts;
}

export const RUN_STATUSES = ["Running", "Paused", "Done", "Failed"] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];
/** A run older than this can't be resumed: plan the file again as a new run. */
export const RESUME_DAYS = 30;
/** The run record's counts and status are saved every this many records. */
export const SAVE_EVERY = 25;

export const resumable = (run: { status: string; startedAt: string }, now: Date) =>
  (run.status === "Running" || run.status === "Paused") &&
  now.getTime() - new Date(run.startedAt).getTime() <= RESUME_DAYS * 86_400_000;

// ── Reset ────────────────────────────────────────────────────────────────────────────────────

/** The records the Reset removes, counted. The System log (`change_entry`) is never among them. */
export interface StoreCounts {
  lines: number;
  pickTickets: number;
  pickups: number;
  shippingRows: number;
  shipments: number;
  preRoutes: number;
}

/**
 * Whether the Reset is offered: on staging (and locally) always; on production only while the store
 * holds no line, pickup, shipping row, shipment or pre-route at all — once one exists it is gone.
 */
export const resetOffered = (appEnv: string, c: StoreCounts) =>
  appEnv !== "production" || c.lines + c.pickups + c.shippingRows + c.shipments + c.preRoutes === 0;

/** The Reset's System log entry. */
export const resetText = (who: string, c: StoreCounts) =>
  `Reset by ${who}: ${c.lines} lines, ${c.pickTickets} pick-ticket copies, ${c.pickups} pickups, ${c.shippingRows} shipping rows, ${c.shipments} shipments, ${c.preRoutes} pre-routes removed`;

// ── The records a run creates ────────────────────────────────────────────────────────────────
// Built here, so the checks run each through the platform's CRUD schema (`crudInputSchema`): a
// required text is `trim().min(1)` there, and an empty one is a 400 before any record is written.

/** What a run's record says before its end: `summary` and `fileHash` are NOT NULL in 020. */
export const RUN_SUMMARY_PENDING = "Running: the summary is written when the run ends";
export const NO_FILE_HASH = "none";

/** A new run's record: its file, who, when and its choices; `summary` filled at its end. */
export function runRecordInput(r: {
  fileName: string;
  fileHash: string | undefined;
  startedBy: string;
  startedAt: string;
  cutoff: string;
  tabs: readonly string[];
  counts: string;
}) {
  return {
    fileName: r.fileName.slice(0, 200) || "workbook.xlsx",
    fileHash: (r.fileHash || NO_FILE_HASH).slice(0, 64),
    startedBy: (r.startedBy || "unknown").slice(0, 128),
    startedAt: r.startedAt,
    finishedAt: "",
    status: "Running",
    cutoff: r.cutoff,
    tabs: (r.tabs.join(", ") || "none").slice(0, 500),
    counts: r.counts,
    summary: RUN_SUMMARY_PENDING,
  };
}

/**
 * A new line's first save beside its fields (the PE ones as stored, `storedLineFields`): Approved,
 * reviewed and grouped by the import, its READY DATE and SCHEDULED SHIP DATE as approved — so it reads
 * Approved, not Updated.
 */
export function importLineChange(
  fields: Record<string, unknown>,
  line: Pick<RunLine, "fields">,
  shown: { readyDate: string; schedShip: string },
  at: string,
) {
  return {
    ...fields,
    reviewStatus: "Approved" as const,
    reviewedBy: IMPORT_BY,
    reviewedAt: at,
    groupedBy: IMPORT_BY,
    groupedAt: at,
    approvedReadyDate: line.fields.readyDate ?? (shown.readyDate || null),
    approvedSchedShip: line.fields.schedShip ?? (shown.schedShip || null),
  };
}

/** A pickup's row for a line (PLTS is the line's Pallets, as Shipping keeps it). */
export function shippingRowInput(r: RunPickup["rows"][number], pickupId: string, lineId: string, sortOrder: number) {
  return {
    pickupId,
    lineId,
    wmsNo: r.line.startsWith("WMS|") ? r.line.slice(4) : "",
    ctns: r.ctns,
    plts: null,
    loader: r.loader,
    inTime: r.inTime,
    outTime: r.outTime,
    trailer: r.trailer,
    wmsAsnTrip: r.wmsAsnTrip,
    notes: r.notes,
    sortOrder,
    status: "On pickup" as const,
  };
}

/** A list value Step 3 adds. */
export const listValueInput = (v: { list: ListName; value: string }) => ({
  list: v.list,
  value: v.value,
  retired: "No" as const,
});
